import { ReactElement, ReactNode } from 'react';
import { textEditor } from 'components/DataGrid';
import type { GridGroup, GridRow, RowOrGroup } from '../../../types';
import { budgetTitleEditor, cellClassName, GAP } from './style.css';
import { supplierTitleHeader } from 'modules/supplier/supplier/components';
import { CellWrapper } from './CellWrapper';
import { ViewSummaryBudget } from './ViewSummaryBudget';
import { iconBtn } from '../../style.css';
import { Box, Button } from '@ff-it/ui';
import { useToggleAll } from 'modules/campaign/plan/state';
import { EditSupplier } from './EditSupplier';
import { ViewSupplier } from '../../../components/ViewSupplier';
import { byKind, type FieldBranch } from '../createPlanColumn';
import type { PlanClipboardCellConfig, PlanClipboardContext, PlanCellPayload, PlanColumn } from '../../clipboard';
import type { RowPatch } from '../../../diff';
import type { Supplier } from 'modules/supplier/supplier/types';
import { resolveSupplierPatch } from './resolveSupplierPatch';

const BASE_WIDTH = 326;
const icons = {
  expanded: 'folder-open',
  collapsed: 'folder-closed',
  mixed: 'folder-minus',
};
function TitleHeader(): ReactElement {
  const [state, toggleGroup] = useToggleAll();
  return (
    <Box display="flex" alignItems="center" gap="1" flexGrow={1} ml={state === 'no-groups' ? '2' : '0.5'}>
      {state !== 'no-groups' && (
        <Button testId="toggle-group" variant="ghost" onClick={toggleGroup} icon={icons[state]} className={iconBtn} />
      )}
      <Box flexGrow={1}>{supplierTitleHeader}</Box>
    </Box>
  );
}

function wrap(row: RowOrGroup, children: ReactNode): ReactElement {
  return <CellWrapper row={row}>{children}</CellWrapper>;
}

function supplierScalar(row: GridRow): string {
  const { supplier, product_name } = row;
  if (!supplier) return product_name || '';

  const { product, provider, activity } = supplier;
  const oosc = product.outlet_or_service_category;
  const outlet = oosc.vendor && oosc.vendor.name !== oosc.name ? `${oosc.vendor.name} / ${oosc.name}` : oosc.name;
  const productTitle = product_name || product.name || 'Generic product';

  return `${outlet} / ${productTitle}; ${provider.internal_name}; ${activity.name}`;
}

/**
 * Supplier paste — delegates to `resolveSupplierPatch`, which mirrors
 * EditSupplier: skip no-ops, ask "Keep values?" when the target row carries
 * keepable values for the new supplier's direction, and set `_reset`
 * accordingly so the backend clears or preserves dependent fields.
 * Dismissed modal and unchanged value yield `null` (no patch).
 */
async function pasteSupplier(
  payload: PlanCellPayload,
  { row }: { row: GridRow; ctx: PlanClipboardContext },
): Promise<RowPatch | null> {
  if (payload.field !== 'supplier') return null;
  const supplier = (payload.value as Supplier | null) ?? null;
  const result = await resolveSupplierPatch(row, supplier);
  return result.kind === 'patch' ? result.patch : null;
}

const supplierClipboard: PlanClipboardCellConfig<GridRow> = {
  scalar: supplierScalar,
  paste: pasteSupplier,
};

const supplierBranch: FieldBranch<GridRow> = {
  field: 'supplier',
  clipboard: supplierClipboard,
  view: ({ row }) => wrap(row, <ViewSupplier row={row} />),
  edit: ({ row, onClose }) =>
    wrap(
      row,
      <>
        <EditSupplier row={row} onClose={onClose} />
        <ViewSupplier row={row} />
      </>,
    ),
};

export function createTitleColumn(maxDepth: number): PlanColumn {
  return byKind({
    key: 'title',
    name: <TitleHeader />,
    width: BASE_WIDTH + maxDepth * GAP,
    frozen: true,
    className: cellClassName,
    // summary keeps the grid's default cell padding
    summaryClassName: '',
    renderSummary: ViewSummaryBudget,
    branches: {
      group: {
        view: ({ row }) => wrap(row, <ViewGroup row={row} />),
        edit: (props) => wrap(props.row, textEditor({ ...props, className: budgetTitleEditor })),
        // title is a plain string — all clipboard behavior is derived from the field
      },
      smart: supplierBranch,
      media: supplierBranch,
      service: supplierBranch,
    },
  });
}

export function ViewGroup({ row }: { row: GridGroup }): ReactElement {
  return (
    <Box flexGrow={1} display="flex" flexDirection="column" minWidth="0" lineHeight="1">
      {row.title ? row.title : <Box fontWeight="regular" color="gray500">{`Unnamed group ${row.id}`}</Box>}
    </Box>
  );
}
